import { useEffect } from "react";

/**
 * Injects one or more JSON-LD structured-data blobs into <head>, and
 * removes them on unmount. Pass any serializable schema.org object(s).
 */
export function useJsonLd(data: unknown | unknown[], deps: unknown[] = []) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld-page", "true");
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const SITE_ORIGIN = "https://relark.dk";

/**
 * Canonical Organization schema for Relark.
 * Includes E-E-A-T signals (founder, knowsAbout, contactPoint, areaServed)
 * so AI assistants and search engines can attribute expertise correctly.
 */
export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: "Relark",
  legalName: "Relark ApS",
  url: SITE_ORIGIN,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_ORIGIN}/favicon.png`,
    width: 512,
    height: 512,
  },
  sameAs: [
    "https://relark.dk",
    "https://relark.se",
    "https://relark.de",
    "https://relark.app",
    "https://relark.eu",
  ],
  description:
    "The sparring partner that's always ready — psychologically grounded leadership help for middle managers with 5–25 reports. Concrete conversation script and bias check in under 5 minutes. Built on Big Five, hosted in Paris, EU AI Act-ready from day one.",
  slogan: "The sparring partner that's always ready",
  foundingDate: "2026",
  foundingLocation: {
    "@type": "Place",
    address: { "@type": "PostalAddress", addressCountry: "DK" },
  },
  founder: {
    "@type": "Person",
    name: "Martin",
    jobTitle:
      "Founder · Officer (Danish Defence) & Master of Organisational Psychology (MPA)",
    description:
      "Officer in the Danish Defence and Master of Organisational Psychology (MPA). Has sat in both seats — as the manager without sparring, and as the psychologist who saw the consequences.",
  },
  areaServed: ["DK", "NO", "SE", "DE", "NL", "FI"],
  knowsAbout: [
    "Leadership psychology",
    "Middle management coaching",
    "Difficult conversations",
    "Psychological safety",
    "Manager coaching",
    "Big Five (OCEAN) personality assessment",
    "Self-determination theory",
    "Thomas-Kilmann conflict modes",
    "Workplace dynamics",
    "EU AI Act compliance",
    "LMX theory",
  ],
  knowsLanguage: ["en", "da", "de", "fr", "es", "pt", "nb", "sv"],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "hello@relark.dk",
      availableLanguage: ["en", "da"],
    },
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "hello@relark.dk",
      availableLanguage: ["en", "da"],
    },
  ],
};

/**
 * WebSite schema with SearchAction so Google can render a sitelinks searchbox.
 * The query placeholder is replaced by the search query at runtime.
 */
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  url: SITE_ORIGIN,
  name: "Relark",
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  inLanguage: ["en", "da", "de", "fr", "es", "pt", "nb", "sv"],
};

/**
 * Build a schema.org BreadcrumbList from an ordered list of crumbs.
 * Each crumb is { name, path } where path is absolute (e.g. "/pricing").
 */
export function buildBreadcrumbJsonLd(
  crumbs: { name: string; path: string }[],
  origin = SITE_ORIGIN
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${origin}${c.path === "/" ? "" : c.path}` || `${origin}/`,
    })),
  };
}

/**
 * Build a FAQPage schema from a list of question/answer pairs.
 * Use directly inside useJsonLd([...]) alongside other schemas.
 */
export function buildFaqJsonLd(
  faqs: { question: string; answer: string }[],
  pageUrl?: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(pageUrl ? { "@id": `${pageUrl}#faq` } : {}),
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/**
 * Build a HowTo schema for step-by-step guides (e.g. pain page scripts).
 * Each step gets its own HowToStep entity so AI assistants can cite them.
 */
export function buildHowToJsonLd(input: {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
  totalTime?: string; // ISO 8601 duration, e.g. "PT2M"
  pageUrl?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    ...(input.pageUrl ? { "@id": `${input.pageUrl}#howto` } : {}),
    name: input.name,
    description: input.description,
    ...(input.totalTime ? { totalTime: input.totalTime } : {}),
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/**
 * Build a SpeakableSpecification block — used by voice assistants
 * (Google Assistant, Siri) to know which parts of the page to read aloud.
 * Pass CSS selectors that uniquely identify the speakable elements.
 */
export function buildSpeakableJsonLd(cssSelector: string[], pageUrl?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    ...(pageUrl ? { "@id": `${pageUrl}#speakable` } : {}),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector,
    },
  };
}
